import React from 'react';
import { useTranslation } from 'react-i18next';
import { useScrollReveal } from '../../hooks';
import { PRICING, UPGRADES, CONTACT_INFO } from '../../constants';
import './MenuPricing.css';

/**
 * Menu pricing section component
 * With modern card design and animations
 */
const MenuPricing: React.FC = () => {
	const { t } = useTranslation();
	const { ref: sectionRef, isVisible } = useScrollReveal({ threshold: 0.1 });

	return (
		<section className="menu-pricing" id="menu" ref={sectionRef as React.RefObject<HTMLElement>}>
			<div className={`pricing-container ${isVisible ? 'visible' : ''}`}>
				{/* Header */}
				<div className="section-header">
					<h2>{t('menu.title', 'Pricing & Service')}</h2>
				</div>

				{/* Main Pricing Card */}
				<div className="pricing-main animate-fade-in-up">
					{/* Price Display */}
					<div className="price-display">
						<div className="price-tag">
							<span className="currency">$</span>
							<span className="amount">{PRICING.PER_PERSON}</span>
							<span className="unit">/{t('pricing.perPerson', 'person')}</span>
						</div>
						<p className="minimum">
							<strong>${PRICING.MINIMUM_ORDER}</strong> {t('pricing.minimum', 'minimum for all parties')}
						</p>
					</div>

					{/* Includes */}
					<div className="pricing-includes">
						<div className="include-item">
							<span className="icon">🥗</span>
							<span>{t('menu.includes.salad', 'Fresh Salad')}</span>
						</div>
						<div className="include-item">
							<span className="icon">🍚</span>
							<span>{t('menu.includes.rice', 'Fried Rice')}</span>
						</div>
						<div className="include-item">
							<span className="icon">🥦</span>
							<span>{t('menu.includes.vegetables', 'Hibachi Vegetables')}</span>
						</div>
						<div className="include-item">
							<span className="icon">🎭</span>
							<span>{t('menu.includes.show', 'Game & Show')}</span>
						</div>
					</div>

					{/* Kids Pricing */}
					<p className="kids-note">
						💰 ${PRICING.KIDS_PRICE} {t('pricing.kids', 'per child 12 and under')}
					</p>
				</div>

				{/* Protein Section */}
				<div className="protein-section">
					<h3>🥩 {t('menu.protein.title', 'Protein Choices')} (2 {t('pricing.perPerson', 'per person')})</h3>
					<div className="protein-grid">
						<div className="protein-item">
							<span className="name">{t('menu.chicken', 'Chicken')}</span>
							<span className="badge">{t('menu.protein.included', 'Included')}</span>
						</div>
						<div className="protein-item">
							<span className="name">{t('menu.steak', 'Steak')}</span>
							<span className="badge">{t('menu.protein.included', 'Included')}</span>
						</div>
						<div className="protein-item">
							<span className="name">{t('menu.shrimp', 'Shrimp')}</span>
							<span className="badge">{t('menu.protein.included', 'Included')}</span>
						</div>
						<div className="protein-item">
							<span className="name">{t('menu.salmon', 'Salmon')}</span>
							<span className="badge">{t('menu.protein.included', 'Included')}</span>
						</div>
						<div className="protein-item">
							<span className="name">{t('menu.tofu', 'Tofu')}</span>
							<span className="badge">{t('menu.protein.included', 'Included')}</span>
						</div>
					</div>
				</div>

				{/* Premium Upgrades */}
				<div className="upgrades-section">
					<h3>⭐ {t('menu.upgrades.title', 'Premium Upgrades')}</h3>
					<div className="upgrade-grid">
						<div className="upgrade-item">
							<span className="name">{t('menu.upgrades.filet', 'Filet Mignon')}</span>
							<span className="price">+${UPGRADES.FILET_MIGNON}</span>
						</div>
						<div className="upgrade-item">
							<span className="name">{t('menu.upgrades.lobster', 'Lobster')}</span>
							<span className="price">+${UPGRADES.LOBSTER}</span>
						</div>
						<div className="upgrade-item">
							<span className="name">{t('menu.upgrades.ribeye', 'Premium Ribeye')}</span>
							<span className="price">+${UPGRADES.PREMIUM_RIBEYE}</span>
						</div>
						<div className="upgrade-item">
							<span className="name">{t('menu.upgrades.shrimp', 'Jumbo Shrimp')}</span>
							<span className="price">+${UPGRADES.JUMBO_SHRIMP}</span>
						</div>
						<div className="upgrade-item">
							<span className="name">{t('menu.upgrades.salmon', 'Wild Salmon')}</span>
							<span className="price">+${UPGRADES.SALMON}</span>
						</div>
						<div className="upgrade-item">
							<span className="name">{t('menu.upgrades.scallops', 'Large Scallops')}</span>
							<span className="price">+${UPGRADES.LARGE_SCALLOPS}</span>
						</div>
					</div>
				</div>

				{/* Add-ons */}
				<div className="addons-section">
					<h3>➕ {t('menu.addons.title', 'Add-ons')}</h3>
					<div className="addon-grid">
						<div className="addon-item">
							<span>🥟 {t('menu.addons.gyoza', 'Gyoza (12pcs)')}</span>
							<span className="price">${UPGRADES.GYOZA_12PCS}</span>
						</div>
						<div className="addon-item">
							<span>🫛 {t('menu.addons.edamame', 'Edamame')}</span>
							<span className="price">${UPGRADES.EDAMAME}</span>
						</div>
						<div className="addon-item">
							<span>🍜 {t('menu.addons.noodles', 'Noodles')}</span>
							<span className="price">${UPGRADES.NOODLES}</span>
						</div>
						<div className="addon-item">
							<span>🥩 {t('menu.addons.thirdProtein', '3rd Protein')}</span>
							<span className="price">${UPGRADES.THIRD_PROTEIN}</span>
						</div>
					</div>
				</div>

				{/* Coupon Section */}
				<div className="coupon-section">
					<h3>🎁 {t('menu.coupon.title', 'Special Coupons')}</h3>
					<p className="coupon-subtitle">{t('menu.coupon.subtitle', 'Sunday to Friday parties only!')}</p>
					<div className="coupon-grid">
						<div className="coupon-item">
							<span className="discount">$30</span>
							<span className="range">{t('coupon.under15', 'under 15 people')}</span>
						</div>
						<div className="coupon-item">
							<span className="discount">$60</span>
							<span className="range">{t('coupon.range15to25', '15-25 people')}</span>
						</div>
						<div className="coupon-item">
							<span className="discount">$90</span>
							<span className="range">{t('coupon.range25to35', '25-35 people')}</span>
						</div>
						<div className="coupon-item">
							<span className="discount">$120</span>
							<span className="range">{t('coupon.over35', '35+ people')}</span>
						</div>
					</div>
					<p className="coupon-note">
						📧 {t('menu.coupon.email', 'Email us at')} <a href={`mailto:${CONTACT_INFO.email}`}>{CONTACT_INFO.email}</a>
					</p>
				</div>

				{/* Note */}
				<div className="pricing-note">
					<p>⚠️ <strong>{t('menu.note.outdoor', 'We cook outside only.')}</strong></p>
					<p>{t('menu.note.weather', 'We provide services rain or shine, as long as there is a dry area for the chef to cook under.')}</p>
				</div>
			</div>
		</section>
	);
};

export default MenuPricing;
